import { MouseEventHandler } from 'react';
import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalProps,
  Spinner,
} from '@chakra-ui/react';
import CancelIcon from '@mui/icons-material/Cancel';
import CreateIcon from '@mui/icons-material/Create';
import useBreakpoint from '@/hooks/useBreakpoint';
import Button from '@/trio-ui/Button';
import * as Styles from './Modal.styles';
import { VariantColor, VariantStyle } from '../Button/Button.component';

export type ModalCustomProps = Omit<ModalProps, 'children'> & {
  onConfirmClick?: MouseEventHandler<HTMLButtonElement>;
  bodyRender: JSX.Element;
  modalTitle?: string;
  onConfirmLabel?: string;
  isButtonDisabled?: boolean;
  hideCancelButton?: boolean;
  hideConfirmButton?: boolean;
  modalId?: string;
  headerIcon?: typeof CreateIcon;
  onCancelButtonVariantStyle?: VariantStyle;
  onConfirmButtonVariant?: VariantColor;
  onCancelButtonVariant?: VariantColor;
  revertButtons?: boolean;
  onCancelLabel?: string;
  onConfirmButtonVariantStyle?: VariantStyle;
  isLoading?: boolean;
};

const Modal = function ({
  isOpen,
  onClose,
  onConfirmClick,
  bodyRender: BodyRender,
  modalTitle,
  onConfirmLabel = 'Confirm',
  isButtonDisabled,
  modalId = 'modal',
  hideCancelButton,
  hideConfirmButton,
  headerIcon: HeaderIcon,
  onCancelButtonVariantStyle = 'outline',
  onConfirmButtonVariant,
  onCancelButtonVariant,
  onCancelLabel = 'Cancel',
  revertButtons = false,
  onConfirmButtonVariantStyle,
  isLoading = false,
  ...props
}: ModalCustomProps): JSX.Element {
  const showConfirmButton = !!onConfirmClick && !hideConfirmButton;
  const showCancelButton = !hideCancelButton;
  const { isMobile } = useBreakpoint();

  const mobileContentSx = isMobile
    ? {
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        margin: 0,
        maxWidth: '100%',
        maxHeight: '90vh',
        borderRadius: '16px 16px 0 0',
        border: '1px solid #EBEBEB',
        boxShadow: '0px -4px 24px rgba(0, 0, 0, 0.12)',
        overflowY: 'auto' as const,
        WebkitOverflowScrolling: 'touch' as const,
      }
    : {
        border: '1px solid #EBEBEB',
        boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.12)',
        borderRadius: '16px',
      };

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} {...props}>
      <ModalOverlay bg={isMobile ? 'rgba(0,0,0,0.3)' : 'none'} />
      <ModalContent
        containerProps={{ style: { flexWrap: 'wrap' } }}
        sx={mobileContentSx}
        data-testid={modalId}
      >
        <Styles.CustomHeader sx={{ padding: isMobile ? '20px 16px 0px' : '32px 32px 0px' }}>
          {modalTitle && HeaderIcon && (
            <Styles.IconArea>
              <HeaderIcon sx={{ width: '18px' }} />
            </Styles.IconArea>
          )}
          {modalTitle && <Styles.HeaderTitle>{modalTitle}</Styles.HeaderTitle>}
          <Styles.CloseButton onClick={onClose}>
            <CancelIcon sx={{ color: '#ABACAF', width: '100%' }} />
          </Styles.CloseButton>
        </Styles.CustomHeader>

        <ModalBody sx={{ padding: isMobile ? '0 16px 20px' : '0 32px 24px' }}>{BodyRender}</ModalBody>
        {(showConfirmButton || showCancelButton) && (
          <ModalFooter
            sx={{
              padding: isMobile ? '0 16px 24px' : '0 0 32px',
              gap: '10px',
              flexDirection: revertButtons ? 'row-reverse' : 'unset',
            }}
            justifyContent="center"
          >
            {isLoading && <Spinner size="md" />}
            {!isLoading && showConfirmButton && (
              <Button
                variantColor={onConfirmButtonVariant}
                disabled={isButtonDisabled}
                size={isMobile ? 'large' : 'medium'}
                onClick={onConfirmClick}
                variantStyle={onConfirmButtonVariantStyle}
              >
                {onConfirmLabel}
              </Button>
            )}
            {!isLoading && showCancelButton && (
              <Button
                size={isMobile ? 'large' : 'medium'}
                variantColor={onCancelButtonVariant}
                variantStyle={onCancelButtonVariantStyle}
                onClick={onClose}
              >
                {onCancelLabel}
              </Button>
            )}
          </ModalFooter>
        )}
      </ModalContent>
    </ChakraModal>
  );
};

export default Modal;
